import { db, functions } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { StaffInvitation, StaffRole } from '../types/user';

const COLLECTION = 'staffInvitations';

function generateToken(): string {
  const arr = new Uint8Array(24);
  crypto.getRandomValues(arr);
  return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Create a staff invitation (root-only action).
 * Returns the invitation token to include in the invite email.
 */
export async function createStaffInvitation(
  email: string,
  role: StaffRole,
  invitedBy: string,
  invitedByName: string
): Promise<StaffInvitation> {
  const token = generateToken();
  const ref = doc(collection(db, COLLECTION));
  const invitation: Omit<StaffInvitation, 'id'> = {
    email: email.toLowerCase().trim(),
    role,
    token,
    status: 'pending',
    invitedBy,
    invitedByName,
    createdAt: new Date().toISOString(),
  };
  await setDoc(ref, { ...invitation, id: ref.id, _createdAt: serverTimestamp() });
  return { ...invitation, id: ref.id };
}

/**
 * Look up a staff invitation by token (used on the acceptance page).
 * Returns null if not found or already accepted.
 */
export async function getStaffInvitationByToken(token: string): Promise<StaffInvitation | null> {
  const q = query(collection(db, COLLECTION), where('token', '==', token), where('status', '==', 'pending'));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return snap.docs[0].data() as StaffInvitation;
}

/**
 * Mark a staff invitation as accepted.
 */
export async function acceptStaffInvitation(invitationId: string): Promise<void> {
  await updateDoc(doc(db, COLLECTION, invitationId), {
    status: 'accepted',
    acceptedAt: new Date().toISOString(),
  });
}

/**
 * Get all staff invitations (for the root user management page).
 */
export async function getAllStaffInvitations(): Promise<StaffInvitation[]> {
  const snap = await getDocs(collection(db, COLLECTION));
  return snap.docs.map(d => d.data() as StaffInvitation);
}

/**
 * Permanently delete a staff invitation (any status).
 */
export async function deleteStaffInvitation(invitationId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, invitationId));
}

/**
 * Send the invitation email via Cloud Function.
 */
export async function sendStaffInvitationEmail(invitationId: string): Promise<void> {
  const fn = httpsCallable(functions, 'adminSendStaffInvitationEmail');
  await fn({ invitationId });
}
